import { useRef } from 'react'

import { getAIConversation } from '../../../../api'
import { isAIConversationGenerationRunning } from '../../../../utils/aiAgents'
import { isApiNotFoundStatus } from '../../../../utils/api'
import { sleep } from '../../../../utils/async'
import { GRETA_AI_CHAT_STATUS_POLL_MS } from '../../GretaAIChat.constants'

export function useGretaAIChatGenerationPoll() {
  const pollPromisesRef = useRef(new Map())

  const pollCancelledRef = useRef(new Set())

  async function checkGenerationSettled(conversationId, options) {
    try {
      const response = await getAIConversation({ id: conversationId, options })

      return !isAIConversationGenerationRunning(response)
    } catch (reason) {
      return isApiNotFoundStatus(reason?.response?.status)
    }
  }

  async function pollStep(conversationId) {
    await sleep(GRETA_AI_CHAT_STATUS_POLL_MS)

    if (!pollCancelledRef.current.has(conversationId)) {
      const isSettled = await checkGenerationSettled(conversationId)

      if (!isSettled && !pollCancelledRef.current.has(conversationId)) {
        await pollStep(conversationId)
      }
    }
  }

  function pollUntilSettled(conversationId) {
    if (!pollPromisesRef.current.has(conversationId)) {
      pollCancelledRef.current.delete(conversationId)

      const promise = pollStep(conversationId).finally(() => {
        if (pollPromisesRef.current.get(conversationId) === promise) {
          pollPromisesRef.current.delete(conversationId)
        }
      })

      pollPromisesRef.current.set(conversationId, promise)
    }

    return pollPromisesRef.current.get(conversationId)
  }

  function stopPolling(conversationId) {
    pollCancelledRef.current.add(conversationId)
  }

  function getIsPolling(conversationId) {
    return pollPromisesRef.current.has(conversationId)
  }

  return {
    checkGenerationSettled,
    getIsPolling,
    pollUntilSettled,
    stopPolling,
  }
}
